import React from 'react';
import { Box, Typography } from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../auth/AuthContext';
import { db } from '../../config/firebase';
import { companyMediaCacheKey, getCompanyLogoUrl, getPlatformLogoUrl, resolveCompanyId } from '../../utils/companyIdentity';

const pickLogo = (companyData, userData, cachedLogo = '') => {
    const hasLoadedCompany = Boolean(companyData && resolveCompanyId(companyData));
    return getCompanyLogoUrl(companyData || {}) || (hasLoadedCompany ? '' : cachedLogo) || '';
};

const pickName = (companyData, userData, cachedName = '') => (
    companyData?.companyName || companyData?.name || userData?.companyName || userData?.company?.companyName || cachedName || 'TELCOFAIR'
);

const LoadingSpinner = ({ label = 'Loading workspace…', message, compact = false }) => {
    const { companyData, userData, platformSettings, currentUser } = useAuth();
    const companyId = resolveCompanyId({ companyData, userData, currentUser });
    const cachedLogo = typeof window !== 'undefined' && companyId ? window.localStorage.getItem(companyMediaCacheKey(companyId, 'logo')) || '' : '';
    const cachedPlatformLogo = typeof window !== 'undefined' ? window.localStorage.getItem('telcofair_platform_logo') || '' : '';
    const platformLogo = getPlatformLogoUrl(platformSettings || {});
    const cachedName = typeof window !== 'undefined' && companyId ? window.localStorage.getItem(companyMediaCacheKey(companyId, 'name')) || '' : '';
    const [resolved, setResolved] = React.useState(() => ({
        logo: currentUser ? pickLogo(companyData, userData, cachedLogo) : (platformLogo || cachedPlatformLogo),
        name: pickName(companyData, userData, cachedName),
    }));

    React.useEffect(() => {
        const directLogo = currentUser
            ? pickLogo(companyData, userData, cachedLogo)
            : (platformLogo || cachedPlatformLogo);
        const directName = currentUser
            ? pickName(companyData, userData, cachedName)
            : (platformSettings?.platformName || platformSettings?.companyName || platformSettings?.name || 'TELCOFAIR');
        setResolved({ logo: directLogo, name: directName });
        if (directLogo || !currentUser) return undefined;

        const companyId = companyData?.id || companyData?.companyId || userData?.companyId || userData?.company?.id || userData?.company?.companyId;
        if (!companyId) return undefined;
        let active = true;
        getDoc(doc(db, 'companies', String(companyId)))
            .then((snap) => {
                if (!active || !snap.exists()) return;
                const company = snap.data() || {};
                const logo = getCompanyLogoUrl(company);
                const name = company.companyName || company.name || directName || 'TELCOFAIR';
                if (logo && typeof window !== 'undefined') window.localStorage.setItem(companyMediaCacheKey(companyId, 'logo'), logo);
                if (name && typeof window !== 'undefined') window.localStorage.setItem(companyMediaCacheKey(companyId, 'name'), name);
                setResolved({ logo, name });
            })
            .catch(() => {});
        return () => { active = false; };
    }, [companyData, userData, cachedLogo, cachedName, cachedPlatformLogo, platformLogo, platformSettings, currentUser, companyId]);

    const logo = resolved.logo;
    const companyName = resolved.name || 'TELCOFAIR';
    const displayLabel = message || label;

    return (
        <Box className="tf-company-loader" sx={{
            minHeight: compact ? 300 : '100vh', display: 'grid', placeItems: 'center', px: 2, py: compact ? 2 : 0,
            background: compact ? 'transparent' : `radial-gradient(circle at 20% 15%, rgba(var(--primary-rgb), .10), transparent 32%), radial-gradient(circle at 85% 80%, rgba(var(--secondary-rgb), .08), transparent 28%), #F1F6F9`,
        }}>
            <Box sx={{
                width: compact ? 'min(330px, 92vw)' : 'min(360px, 92vw)', p: compact ? 3 : 4, textAlign: 'center', position: 'relative', overflow: 'hidden',
                background: 'rgba(255,255,255,.88)', backdropFilter: 'blur(20px) saturate(160%)', WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                border: '1px solid rgba(255,255,255,.82)', borderRadius: '24px',
                boxShadow: '0 22px 60px rgba(25,57,78,.12), inset 0 1px 0 rgba(255,255,255,.94)',
            }}>
                <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: .22, backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(var(--primary-rgb),.22) 1px, transparent 0)', backgroundSize: '18px 18px', maskImage: 'linear-gradient(135deg,transparent 20%,#000 100%)' }} />
                <Box sx={{ position: 'relative', width: 92, height: 92, mx: 'auto', mb: 2.25 }}>
                    <Box sx={{
                        position: 'absolute', inset: 7, borderRadius: '23px', display: 'grid', placeItems: 'center', overflow: 'hidden',
                        background: 'linear-gradient(145deg, rgba(255,255,255,.99), rgba(255,255,255,.78))',
                        border: '1px solid rgba(255,255,255,.94)', boxShadow: '0 14px 34px rgba(var(--primary-rgb), .22)', animation: 'loaderCore 2s ease-in-out infinite',
                    }}>
                        {logo ? <img src={logo} alt={companyName} style={{ width: '82%', height: '82%', objectFit: 'contain' }} /> : <Box sx={{ width: 44, height: 44, borderRadius: '14px', background: 'linear-gradient(135deg,var(--primary),var(--secondary))', boxShadow: '0 9px 22px rgba(var(--primary-rgb),.22)' }} />}
                    </Box>
                    {[0,1,2,3,4,5].map((i) => (
                        <Box key={i} sx={{
                            position: 'absolute', width: 9, height: 9, borderRadius: '3px',
                            left: `${42 + Math.cos((i / 6) * Math.PI * 2) * 36}px`, top: `${42 + Math.sin((i / 6) * Math.PI * 2) * 36}px`,
                            background: i % 2 ? 'var(--secondary)' : 'var(--primary)', opacity: .48,
                            boxShadow: '0 4px 12px rgba(var(--primary-rgb), .2)', animation: `loaderFragment 2s ease-in-out ${i * .11}s infinite`,
                        }} />
                    ))}
                </Box>
                <Typography sx={{ position: 'relative', fontSize: '1.05rem', fontWeight: 900, color: '#182235', letterSpacing: '-.02em' }}>{companyName}</Typography>
                <Typography sx={{ position: 'relative', mt: .55, fontSize: '.78rem', color: '#6D7C91', fontWeight: 650 }}>{displayLabel}</Typography>
                <Box sx={{ position: 'relative', mt: 2.4, height: 4, borderRadius: 999, overflow: 'hidden', bgcolor: 'rgba(25,57,78,.05)' }}>
                    <Box sx={{ width: '42%', height: '100%', borderRadius: 999, background: 'linear-gradient(90deg, var(--primary), var(--secondary), var(--accent))', animation: 'loaderBar 1.55s ease-in-out infinite' }} />
                </Box>
            </Box>
            <style>{`@keyframes loaderCore{0%,100%{transform:scale(.98)}50%{transform:scale(1.04)}}@keyframes loaderFragment{0%,100%{transform:translate(0,0) scale(.72);opacity:.22}50%{transform:translate(3px,-5px) scale(1);opacity:.72}}@keyframes loaderBar{0%{transform:translateX(-115%)}55%,100%{transform:translateX(260%)}}`}</style>
        </Box>
    );
};

export default LoadingSpinner;
